import { useCallback, useEffect, useRef } from "react";
import { type Topic } from "@foundation/artemis-client";
import { useArtemisClient } from "~hooks/useArtemisClient";
import { marketBarToChartPoint } from "~utils/ticker-chart";
import type {
  ArtemisMarketDataMessage,
  ChartStore,
  ChartType,
  OhlcvPoint,
  PricePoint,
  TickerChartApi,
} from "~types/ticker-chart";
import {
  ARTEMIS_PROVIDER_DATATYPE,
  ARTEMIS_PROVIDER_MARKET_DATA,
} from "~constants/ticker-chart";

interface UseRealtimeMarketDataOptions {
  readonly ticker: string;
  readonly chartType: ChartType;
  readonly enableRealTime: boolean;
  /** Pass the store directly when using ChartContainer (e.g. SparkLine). */
  readonly store?: ChartStore;
  /** Callback receiving each converted point (e.g. for local state management). */
  readonly onPoint?: (point: PricePoint | OhlcvPoint) => void;
}

interface UseRealtimeMarketDataResult {
  /**
   * Pass to `<TickerChart onReady={onReady} />` when no store is provided.
   * When a store is provided, this is a no-op.
   */
  readonly onReady: (api: TickerChartApi) => void;
}

/**
 * Subscribes to Artemis MARKET_DATA for a ticker and pushes intraday bars
 * into the chart via appendData.
 *
 * Three usage patterns:
 * - With `<TickerChart>`: omit `store`, pass the returned `onReady` to the component.
 * - With `<ChartContainer>` (SparkLine): pass `store` directly, ignore `onReady`.
 * - With raw data (SparkLineCell): pass `onPoint` callback to receive converted points.
 */
export function useRealtimeMarketData({
  ticker,
  chartType,
  enableRealTime,
  store,
  onPoint,
}: UseRealtimeMarketDataOptions): UseRealtimeMarketDataResult {
  const chartApiRef = useRef<TickerChartApi | null>(null);

  const onReady = useCallback((api: TickerChartApi) => {
    chartApiRef.current = api;
  }, []);

  const handleMarketBarData = useCallback(
    (topicData: Topic) => {
      const intradayData = (topicData.data as ArtemisMarketDataMessage)
        .intraday;

      if (!intradayData) return;

      const point = marketBarToChartPoint(intradayData, chartType);

      if (onPoint) {
        onPoint(point);
      } else if (store) {
        store.appendData(ticker, [point]);
      } else if (chartApiRef.current) {
        chartApiRef.current.appendData(ticker, [point]);
      }
    },
    [chartType, ticker, store, onPoint]
  );

  const artemisClient = useArtemisClient({
    topicOptions: JSON.stringify({
      [ARTEMIS_PROVIDER_MARKET_DATA]: {
        dataType: ARTEMIS_PROVIDER_DATATYPE.INTRADAY,
      },
    }),
  });

  useEffect(() => {
    if (!artemisClient || !enableRealTime)
      return () => {
        return;
      };

    const unsubscribe = artemisClient.subscribe(
      ARTEMIS_PROVIDER_MARKET_DATA,
      ticker,
      handleMarketBarData
    );

    return () => unsubscribe();
  }, [artemisClient, ticker, enableRealTime, handleMarketBarData]);

  return { onReady };
}
